import { AxiosError } from 'axios';
import { useQuery } from 'react-query';
import { Axios } from '@utils/api/axios-instance';
import { GET_URL } from '@utils/api/endpoints';
import { BaseError } from '@utils/types/shared/base';
import { GlossaryEntry } from '@utils/types/glossary';

// Helper function to parse CSV text into glossary entries
const parseCSVToEntries = (csvText: string): GlossaryEntry[] => {
  if (!csvText || csvText.trim() === '') return [];

  const lines = csvText.split('\n').filter(line => line.trim() !== '');

  return lines.map((line, index) => {
    // Simple CSV parsing - for production, consider using a library like PapaParse
    // to handle quoted fields and special characters properly
    const commaIndex = line.indexOf(',');
    if (commaIndex === -1) {
      return {
        id: index + 1,
        source_term: line.trim(),
        target_term: ''
      };
    }

    const source_term = line.substring(0, commaIndex).trim();
    const target_term = line.substring(commaIndex + 1).trim();

    return {
      id: index + 1,
      source_term,
      target_term
    };
  });
};

export const useGetGlossaryEntries = (glossaryId: number) => {
  const fetchGlossaryEntries = async () => {
    const url = GET_URL(glossaryId.toString()).glossaryEntries;
    if (!url) throw new Error('Glossary entries endpoint not found');

    const { data } = await Axios.get<string>(url, {
      headers: {
        Accept: 'text/csv'
      }
    });

    // Parse the CSV text into entries
    const entries = parseCSVToEntries(data);
    return entries;
  };

  return useQuery<GlossaryEntry[], AxiosError<BaseError>>(
    ['glossaryEntries', glossaryId],
    fetchGlossaryEntries,
    {
      useErrorBoundary: false,
      staleTime: 5 * 60 * 1000,
      enabled: !!glossaryId
    }
  );
};
